import { z } from 'zod'
import { AsyncFn } from '../../common'
import { guidSchema, isoDateStringSchema } from '../../contracts/_common'
import { bzOptional, Guid } from '../common-schemas'
import { PhoneNumberString } from '../PhoneNumbers/Phone'

const MessageDirectionEnum = z.enum(['inbound', 'outbound'])
export type IntegratedMessageDirection = z.infer<typeof MessageDirectionEnum>

const IntegratedMessageStatusEnum = z.enum(['sending', 'sent', 'delivered', 'failed'])

export type IntegratedMessageStatus = z.infer<typeof IntegratedMessageStatusEnum>

export type IntegratedChannelGuid = string

export const IntegratedChannelSchema = z.object({
  integratedChannelGuid: guidSchema,
  companyGuid: guidSchema,
  contactGuid: bzOptional(guidSchema),
  externalPhoneNumber: z.string(),
})

export type IntegratedChannel = z.infer<typeof IntegratedChannelSchema>

export const IntegratedMessageSchema = z.object({
  integratedMessageGuid: guidSchema,
  companyGuid: guidSchema,
  integratedChannelGuid: guidSchema,
  integratedPhoneGuid: guidSchema,
  externalMessageId: bzOptional(z.string()),
  direction: MessageDirectionEnum,
  fromPhoneNumber: z.string(),
  toPhoneNumber: z.string(),
  sentByUserGuid: bzOptional(guidSchema),
  body: bzOptional(z.string()),
  createdAt: isoDateStringSchema,
})

export type IntegratedMessage = z.infer<typeof IntegratedMessageSchema>

export const IntegratedMessageStatusesSchema = z.object({
  integratedMessageStatusGuid: guidSchema,
  integratedMessageGuid: guidSchema,
  companyGuid: guidSchema,
  isDraft: bzOptional(z.boolean()),
  sentAt: bzOptional(isoDateStringSchema),
  deliveredAt: bzOptional(isoDateStringSchema),
  receivedAt: bzOptional(isoDateStringSchema),
  failedAt: bzOptional(isoDateStringSchema),
  errorCode: bzOptional(z.string()),
  errorMessage: bzOptional(z.string()),
  dismissedAt: bzOptional(isoDateStringSchema),
})

export const IntegratedMessageWithStatusesSchema = IntegratedMessageSchema.merge(IntegratedMessageStatusesSchema)

export type IntegratedMessageStatuses = z.infer<typeof IntegratedMessageStatusesSchema>

export type IntegratedMessageWithStatuses = IntegratedMessage & IntegratedMessageStatuses

export type IntegratedMessageWithStatusesWriter = AsyncFn<IntegratedMessageWithStatuses>

export type IntegratedChannelRequest = {
  inboundPhoneNumber: PhoneNumberString
  externalPhoneNumber: PhoneNumberString
  contactGuid?: Guid
}

export type IntegratedChannelAssociation = {
  channel: IntegratedChannel
  integratedPhoneNumberGuid: string
}
export type IntegratedChannelAssociator = AsyncFn<IntegratedChannelRequest, IntegratedChannelAssociation>

type ReadIntegratedChannelRequest =
  | {
      type: 'by-external-phone-number'
      input: {
        externalPhoneNumber: PhoneNumberString
        companyGuid: Guid
        contactGuid?: Guid
      }
    }
  | {
      type: 'by-integrated-channel-guid'
      input: {
        integratedChannelGuid: Guid
      }
    }

export type ReadIntegratedChannel = AsyncFn<ReadIntegratedChannelRequest, IntegratedChannel | undefined>

export type AddContactToIntegratedChannel = AsyncFn<
  {
    integratedChannelGuid: Guid
    contactGuid: Guid
  },
  IntegratedChannel
>

export type CreateIntegratedChannel = AsyncFn<
  {
    externalPhoneNumber: PhoneNumberString
    companyGuid: Guid
    contactGuid?: Guid
  },
  IntegratedChannel
>

export type GetOrCreateIntegratedChannelWithContactRequest = {
  externalPhoneNumber: PhoneNumberString
  companyGuid: Guid
  contactGuid: Guid
}

export type GetOrCreateIntegratedChannelWithPhoneNumberRequest = {
  externalPhoneNumber: PhoneNumberString
  companyGuid: Guid
  contactGuid?: never
}

export type GetOrCreateIntegratedChannelRequest =
  | GetOrCreateIntegratedChannelWithContactRequest
  | GetOrCreateIntegratedChannelWithPhoneNumberRequest

export type GetOrCreateIntegratedChannel = AsyncFn<GetOrCreateIntegratedChannelRequest, IntegratedChannel>

export type GetOrCreateIntegratedChannelWithContact = AsyncFn<
  GetOrCreateIntegratedChannelWithContactRequest,
  IntegratedChannel
>

export type GetOrCreateIntegratedChannelWithPhoneNumber = AsyncFn<
  GetOrCreateIntegratedChannelWithPhoneNumberRequest,
  IntegratedChannel
>

export type FindAllIntegratedChannels = AsyncFn<
  {
    externalPhoneNumber: PhoneNumberString
    companyGuid: Guid
  },
  IntegratedChannel[]
>

export const inferIntegratedChannel = (channels: IntegratedChannel[]): IntegratedChannel | undefined => {
  if (channels.length === 0) return undefined

  // Prefer channels that already have a contact
  const channelWithContact = channels.find(channel => channel.contactGuid)
  if (channelWithContact) {
    return channelWithContact
  }

  return channels[0]
}
